import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapPin, ArrowLeft, Compass, Sparkles, Users, Car, Pencil, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";
import PropertyMap from "@/components/PropertyMap";
import LazyOnView from "@/components/LazyOnView";
import MapLocationPicker from "@/components/MapLocationPicker";
import { useProperties } from "@/hooks/useProperties";
import { useTenant } from "@/hooks/useTenant";
import { countryName } from "@/lib/tenants";
import { useFavorites } from "@/hooks/useFavorites";
import AuthPromptDialog from "@/components/AuthPromptDialog";
import { useAuth } from "@/hooks/useAuth";
import { usePromotions } from "@/lib/monetization";
import { promoteProperties } from "@/lib/browseFilters";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { toast } from "sonner";
import AreaContentEditor from "@/components/admin/AreaContentEditor";
import { useAreas, useInvalidateAreas } from "@/hooks/useAreas";
import type { Area } from "@/lib/areas";
import { upsertAreaRow } from "@/lib/areasApi";
import Seo from "@/components/Seo";
import { areaImageSrc, useSiteAssetResolver } from "@/hooks/useSiteAsset";
import { transformImage } from "@/lib/imageUrl";
import { useBrowseContext } from "@/hooks/useBrowseContext";
import { useCollections } from "@/hooks/useCollections";
import CollectionLinks from "@/components/collections/CollectionLinks";
import ThingsToDoList from "@/components/collections/ThingsToDoList";
import { FRAME } from "@/lib/siteAssets";

const AreaPage = () => {
  const { area } = useParams();
  const tenant = useTenant();
  // Areas come from the database (Admin → Areas), with the code catalog as the
  // fallback. 404 if the URL points at an area not in this city's Locations
  // list (e.g. someone sharing /area/nosara on mysantateresahome.com).
  const { areas, bySlug, isLoading: areasLoading } = useAreas();
  const invalidateAreas = useInvalidateAreas();
  const areaData = area && tenant.areaSlugs.includes(area) ? bySlug.get(area) ?? null : null;
  const resolveAsset = useSiteAssetResolver();
  // The city's uploaded photo for this area (Admin → Site assets → Areas), else
  // the bundled default from src/data/areas.ts.
  const heroImage = areaData ? areaImageSrc(resolveAsset, areaData.slug, areaData.image, { width: 1600, height: 900 }) : null;
  // Phones get a phone-sized hero: three widths of the uploaded photo, the
  // browser picks. The bundled fallback has no variants.
  const heroSrcSet = heroImage && !heroImage.isFallback && heroImage.objectUrl
    ? [800, 1200, 1600].map((w) => `${transformImage(heroImage.objectUrl!, { width: w, height: Math.round((w * 9) / 16) })} ${w}w`).join(", ")
    : undefined;
  const shareCardUrl = heroImage && !heroImage.isFallback ? heroImage.objectUrl : undefined;
  const shareImage = shareCardUrl
    ? transformImage(shareCardUrl, { width: 1200, height: 630, format: "origin" })
    : areaData?.image;
  const { data: allProperties = [] } = useProperties();
  const promotions = usePromotions();
  const { collections } = useCollections();
  const browseCtx = useBrowseContext(allProperties);
  const { toggleFavorite, isFavorite, authPromptOpen, setAuthPromptOpen } = useFavorites();
  const { user } = useAuth();
  const { data: isAdmin = false } = useIsAdmin();
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [editingPin, setEditingPin] = useState(false);
  const [draft, setDraft] = useState<{ lat: number; lng: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingContent, setEditingContent] = useState(false);

  const pinLng = areaData?.hasPin ? areaData.coordinates[0] : null;
  const pinLat = areaData?.hasPin ? areaData.coordinates[1] : null;
  useEffect(() => {
    setCoords(pinLng != null && pinLat != null ? [pinLng, pinLat] : null);
    setEditingPin(false);
    setDraft(null);
    setEditingContent(false);
  }, [areaData?.slug, pinLng, pinLat]);

  // The database row (or catalog entry) is the content; nothing to merge.
  const effective = areaData;

  if (!areaData || !effective) {
    if (areasLoading) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground text-sm">Loading…</div>
      );
    }
    return (
      <div className="text-center min-h-[60vh] flex flex-col items-center justify-center">
        {/* Without this the page is a 200-answering soft 404 wearing the
            shell's site-level meta, and the sitemap advertises it. */}
        <Seo
          title={`Area not found | ${tenant.siteName}`}
          description="This area is not available on this site."
          path={`/areas/${area ?? ""}`}
          noindex
        />
        <h1 className="font-serif text-2xl text-foreground">Area not found</h1>
        <Link to="/areas" className="text-primary underline text-sm mt-3 inline-block">
          Back to Explore Areas
        </Link>
      </div>
    );
  }

  // Paid featured homes first, as on Browse (Oct 20).
  const areaProperties = promoteProperties(
    allProperties.filter((p) => p.location.toLowerCase() === effective.name.toLowerCase()),
    promotions,
    Math.floor(Date.now() / 86_400_000),
  );

  // Nearby areas of this city by simple coordinate distance (pinned ones only).
  const others = areas.filter((a) => a.slug !== effective.slug);
  const nearbyAreas: Area[] = effective.hasPin
    ? others
        .filter((a) => a.hasPin)
        .map((a) => {
          const dx = a.coordinates[0] - effective.coordinates[0];
          const dy = a.coordinates[1] - effective.coordinates[1];
          return { area: a, dist: Math.sqrt(dx * dx + dy * dy) };
        })
        .sort((x, y) => x.dist - y.dist)
        .slice(0, 3)
        .map((x) => x.area)
    : others.slice(0, 3);

  const nearbyHasListings = nearbyAreas.some((a) =>
    allProperties.some((p) => p.location.toLowerCase() === a.name.toLowerCase())
  );


  const InfoCard = ({
    icon: Icon,
    label,
    value,
  }: {
    icon: typeof Users;
    label: string;
    value: string;
  }) => (
    <div className="bg-card border border-border rounded-xl p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
        <p className="text-sm text-foreground font-medium leading-snug">{value}</p>
      </div>
    </div>
  );

  const placeJsonLd = {
    "@context": "https://schema.org",
    "@type": "Place",
    name: effective.name,
    description: effective.seoDescription || effective.shortDescription,
    containedInPlace: { "@type": "Place", name: tenant.addressRegion },
    address: {
      "@type": "PostalAddress",
      addressLocality: effective.name,
      addressRegion: tenant.addressRegion,
      addressCountry: tenant.country,
    },
    ...(areaData.hasPin
      ? { geo: { "@type": "GeoCoordinates", latitude: areaData.coordinates[1], longitude: areaData.coordinates[0] } }
      : {}),
    url: `${tenant.siteUrl}/areas/${areaData.slug}`,
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${tenant.siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "Areas", item: `${tenant.siteUrl}/areas` },
      { "@type": "ListItem", position: 3, name: effective.name, item: `${tenant.siteUrl}/areas/${areaData.slug}` },
    ],
  };

  const itemListJsonLd = areaProperties.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Rentals available in ${effective.name}`,
    itemListElement: areaProperties.slice(0, 25).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${tenant.siteUrl}/properties/${p.slug}`,
      name: p.title,
    })),
  } : null;

  return (
    <div className="pb-24">
      <Seo
        title={effective.seoTitle || `Rentals in ${effective.name}, ${countryName(tenant.country)} | ${tenant.siteName}`}
        description={
          effective.seoDescription ||
          `Find monthly and seasonal rentals in ${effective.name}, ${tenant.regionLabel}, ${countryName(tenant.country)}. ${areaProperties.length} ${areaProperties.length === 1 ? "home" : "homes"} available.`
        }
        path={`/areas/${areaData.slug}`}
        image={shareImage}
        imageAlt={`${effective.name}, ${tenant.regionLabel}`}
        imageWidth={shareCardUrl ? 1200 : undefined}
        imageHeight={shareCardUrl ? 630 : undefined}
        jsonLd={[placeJsonLd, breadcrumbJsonLd, ...(itemListJsonLd ? [itemListJsonLd] : [])]}
      />
      {/* Hero */}
      <section className="relative">
        {/* Background image, sized so text never gets clipped on mobile. md:aspect-[4/1]
            is FRAME.areaHeaderDesktop (the admin previews area photos in it). */}
        <div className="relative w-full h-[32vh] min-h-[220px] max-h-[360px] md:h-auto md:aspect-[4/1] overflow-hidden">
          <img
            src={heroImage?.src ?? areaData.image}
            srcSet={heroSrcSet}
            sizes="100vw"
            alt={effective.name}
            width={1280}
            height={720}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/30 to-foreground/10" />

          {/* Overlay content, flows naturally inside the hero, anchored bottom */}
          <div className="absolute inset-x-0 bottom-0 px-5 pt-8 pb-5 md:p-10">
            <div className="container mx-auto">
              <Link
                to="/areas"
                className="inline-flex items-center gap-1.5 text-sm text-primary-foreground/80 hover:text-primary-foreground mb-3 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> All Areas
              </Link>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-primary-foreground leading-tight">
                {effective.name}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Primary CTA Bar */}
      {areaProperties.length > 0 && (
        <section className="border-b border-border bg-secondary/30">
          <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {areaProperties.length} {areaProperties.length === 1 ? "home" : "homes"} available in {effective.name}
            </p>
            <Button asChild className="w-full sm:w-auto">
              <a href="#listings">See available homes</a>
            </Button>
          </div>
        </section>
      )}

      <div className="container mx-auto px-4">
        {isAdmin && (
          <section className="pt-6">
            {editingContent ? (
              <AreaContentEditor
                area={areaData}
                onSaved={() => {
                  invalidateAreas();
                  setEditingContent(false);
                }}
                onCancel={() => setEditingContent(false)}
              />
            ) : (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditingContent(true)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit area content
                </Button>
              </div>
            )}
          </section>
        )}

        {/* SEO Text, moved to top, right under the hero */}
        {(effective.seoText || effective.shortDescription) && (
        <section className="pt-8 pb-4 sm:pt-12 sm:pb-8">
          <h2 className="font-serif text-2xl font-semibold text-foreground mb-5">
            About {effective.name}
          </h2>
          {(effective.seoText || effective.shortDescription).split("\n\n").map((paragraph, i) => (
            <p key={i} className="text-muted-foreground leading-relaxed mb-4 last:mb-0">
              {paragraph}
            </p>
          ))}
        </section>
        )}

        {/* Quick facts row */}
        {(effective.bestFor || effective.vibe || effective.access) && (
        <section className="pt-6 pb-4 sm:py-10 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            {effective.bestFor && <InfoCard icon={Users} label="Best for" value={effective.bestFor} />}
            {effective.vibe && <InfoCard icon={Sparkles} label="Vibe" value={effective.vibe} />}
            {effective.access && <InfoCard icon={Car} label="Access" value={effective.access} />}
          </div>
        </section>
        )}

        {/* Location & Known For */}
        {(effective.location.bullets.length > 0 || effective.knownFor.length > 0) && (
        <section className="py-4 sm:py-8 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-8">
            {effective.location.bullets.length > 0 && (
            <div className="bg-card border border-border rounded-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-primary" />
                </div>
                <h2 className="font-serif text-xl font-semibold text-foreground">Location</h2>
              </div>
              <ul className="space-y-2.5">
                {effective.location.bullets.map((b, i) => (
                  <li key={i} className="text-muted-foreground text-sm leading-relaxed flex gap-2">
                    <span aria-hidden="true" className="flex h-[1.625em] shrink-0 items-center"><span className="h-1.5 w-1.5 rounded-full bg-primary" /></span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            )}
            {effective.knownFor.length > 0 && (
            <div className="bg-card border border-border rounded-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Compass className="h-4 w-4 text-primary" />
                </div>
                <h2 className="font-serif text-xl font-semibold text-foreground">Known for</h2>
              </div>
              <ul className="space-y-2.5">
                {effective.knownFor.map((k, i) => (
                  <li key={i} className="text-muted-foreground text-sm leading-relaxed flex gap-2">
                    <span aria-hidden="true" className="flex h-[1.625em] shrink-0 items-center"><span className="h-1.5 w-1.5 rounded-full bg-primary" /></span>
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </div>
            )}
          </div>
        </section>
        )}

        <ThingsToDoList items={effective.thingsToDo} heading={`Things to do in ${effective.name}`} className="py-8 border-t border-border" />

        {/* Properties */}
        <section id="listings" className="py-12 border-t border-border scroll-mt-24">
          {areaProperties.length === 0 ? (
            <div className="text-center py-12 bg-secondary/30 rounded-card px-6">
              <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">
                No homes currently listed in {effective.name}
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                {effective.name} is a quieter area with fewer listings, but nearby areas have great options.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-serif text-2xl font-semibold text-foreground">
                  Homes in {effective.name} ({areaProperties.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {areaProperties.map((p, i) => (
                  <PropertyCard key={p.id} property={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggleFavorite} priority={i < 3} />
                ))}
              </div>
            </>
          )}
        </section>


        {/* Collections narrowed to this area, the intent pages crawlers reach from here */}
        <CollectionLinks
          collections={collections}
          properties={allProperties}
          ctx={browseCtx}
          area={{ slug: effective.slug, name: effective.name }}
          variant="cards"
          heading={`Browse ${effective.name} by need`}
          className="py-8 border-t border-border"
        />

        {/* Map - moved below listings, reduced height */}
        {(coords || isAdmin) && (
        <section className="py-10 border-t border-border">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="font-serif text-xl font-semibold text-foreground">Where is {effective.name}?</h2>
            {isAdmin && !editingPin && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => {
                  // No pin yet: start from the city centre so the map opens somewhere sensible.
                  const start = coords
                    ? { lat: coords[1], lng: coords[0] }
                    : { lat: tenant.geoLat ?? 0, lng: tenant.geoLng ?? 0 };
                  setDraft(start);
                  setEditingPin(true);
                }}
              >
                <Pencil className="h-3.5 w-3.5" /> {coords ? "Adjust pin" : "Place pin"}
              </Button>
            )}
          </div>
          <div className="max-w-3xl">
            {editingPin && draft ? (
              <div className="space-y-3">
                <MapLocationPicker
                  lat={draft.lat}
                  lng={draft.lng}
                  onChange={(lat, lng) => setDraft({ lat, lng })}
                  className="h-64"
                />
                <div className="flex items-center gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setEditingPin(false); setDraft(null); }}
                    disabled={saving}
                    className="gap-1.5"
                  >
                    <X className="h-3.5 w-3.5" /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={saving}
                    className="gap-1.5"
                    onClick={async () => {
                      if (!draft || !areaData) return;
                      setSaving(true);
                      try {
                        await upsertAreaRow({
                          tenant_id: tenant.id,
                          slug: areaData.slug,
                          name: areaData.name,
                          lat: draft.lat,
                          lng: draft.lng,
                          updated_by: user?.id ?? null,
                        });
                      } catch {
                        setSaving(false);
                        toast.error("Could not save pin");
                        return;
                      }
                      setSaving(false);
                      setCoords([draft.lng, draft.lat]);
                      setEditingPin(false);
                      setDraft(null);
                      invalidateAreas();
                      toast.success("Pin updated");
                    }}
                  >
                    <Save className="h-3.5 w-3.5" /> Save pin
                  </Button>
                </div>
              </div>
            ) : coords ? (
              // Google Maps (~270 KB of script) loads when the map scrolls into
              // view, as on the listing page, not with the area page.
              <LazyOnView placeholder={<div className="h-48 w-full rounded-card bg-muted/30" />}>
                <PropertyMap
                  key={`${coords?.[0]}-${coords?.[1]}`}
                  coordinates={coords}
                  title={effective.name}
                  className="[&>div:first-child]:!h-48"
                />
              </LazyOnView>
            ) : (
              <p className="text-sm text-muted-foreground">No map pin yet. Use “Place pin” to add one.</p>
            )}
          </div>
        </section>
        )}


        {areaProperties.length > 0 && (
          <section className="py-12 text-center">
            <Link to={`/properties?area=${effective.slug}`}>
              <Button size="lg">View homes in {effective.name}</Button>
            </Link>
          </section>
        )}
      </div>
      <AuthPromptDialog open={authPromptOpen} onOpenChange={setAuthPromptOpen} />
    </div>
  );
};

export default AreaPage;
